import React from 'react';
import PropTypes from 'prop-types';
import { Icon, Link, Spacing, Text } from '@reservamos/elements';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Field } from 'redux-form';
import checkboxRenderer from './CheckboxRenderer';
import InsuranceModal from './InsuranceModal';
import { formatCurrency } from '../utils/currency';
import insuranceETN from '../images/brands-unique/etn/insuranceETN.png';
import 'styles/components/CardCheckbox';
import useCurrencyConfig from '../hooks/whitelabel/useCurrencyConfig';
import useInsuranceConfig from '../hooks/useInsuranceConfig';
import useWhitelabelEnvs from '../hooks/whitelabel/useWhitelabelEnvs';
import { useWhitelabelFeatures } from '../hooks/whitelabel/useWhitelabelFeatures';

/**
 * Whether an insurance update is in flight. A narrow selector rather than `usePurchase`, which
 * calls `.toJS()` on the whole purchase in render; a boolean is referentially stable. It also
 * replaces the `isLoading` prop the component's only caller never passed, despite the propType
 * declaring it required.
 * @param {object} state - Redux state
 * @returns {boolean} - True while the purchase's insurance is being updated
 */
const selectUpdatingInsurance = (state) => Boolean(state.purchase.get('updatingInsurance'));

/**
 * Decimals to show for a premium, matching what AncillariesSwitch does for the checkout switch:
 * two when the tenant forces them, otherwise only when the amount actually has cents.
 * @param {number} amount - Premium for one leg
 * @param {boolean} forceDecimals - `SHOW_INSURANCE_DECIMALS`
 * @returns {number} - 0 or 2
 */
const decimalsFor = (amount, forceDecimals) => {
  if (forceDecimals) return 2;

  return amount % 1 === 0 ? 0 : 2;
};

/**
 * Per-passenger insurance card.
 * One card per passenger, one checkbox per priced leg: a traveller can insure the outbound leg,
 * the return, both or neither, which is what `per_passenger_per_way` bills and what the
 * transporter's `assists[]` expects seat by seat.
 *
 * The card is a heading, a link to the insurer's conditions and the leg checkboxes — no coverage
 * list and no modal, which is what product settled on. The layout is expected to change, so it
 * stays flat rather than growing an abstraction a redesign would have to be unpicked from.
 *
 * A brand with no entry in `useInsuranceConfig`'s title map keeps the legacy rendering — the ETN
 * icon and `InsuranceModal` — byte for byte.
 *
 * @param {Object} props - Component props
 * @param {string} props.passenger - Passenger identifier, the redux-form field prefix
 * @param {number} props.insuranceOutgoingUnitAmount - Premium for the outbound leg
 * @param {number} props.insuranceIncomingUnitAmount - Premium for the return leg
 * @param {boolean} props.disabled - Whether the checkboxes are locked
 * @returns {React.Component} - Rendered component
 */
const PassengerInsuranceToggle = ({
  passenger,
  insuranceOutgoingUnitAmount,
  insuranceIncomingUnitAmount,
  disabled,
}) => {
  const { t } = useTranslation('purchase');
  const { currency, locale } = useCurrencyConfig();
  const { brand } = useWhitelabelEnvs();
  const { SHOW_INSURANCE_DECIMALS } = useWhitelabelFeatures();
  const { brandIcon, detailsUrl, titleContext } = useInsuranceConfig();
  const updatingInsurance = useSelector(selectUpdatingInsurance);

  // A brand with no entry in the title map has no configuration behind it either, so it keeps
  // what it renders today. Keying this on the brand rather than on absent configuration matters:
  // rochoa itself ships with no icon and may ship with no conditions URL.
  const isBrandAware = titleContext !== 'coverage';
  const icon = isBrandAware ? brandIcon : insuranceETN;

  const isLocked = disabled || updatingInsurance;
  const hasIncomingInsurance = insuranceIncomingUnitAmount > 0;
  const departureInsuranceLabel = hasIncomingInsurance
    ? 'label.departing_trip'
    : 'label.add_trip_insurance';

  /**
   * Resolve a leg's label under the brand's context, with its own premium.
   * i18next falls back to the context-less key for any brand without one.
   * @param {string} key - Translation key
   * @param {number} amount - Premium for that leg
   * @returns {string} - Translated label
   */
  const legLabel = (key, amount) =>
    t(key, {
      context: brand,
      cost: formatCurrency(amount, decimalsFor(amount, SHOW_INSURANCE_DECIMALS), currency, locale),
    });

  return (
    <div className="bg-white rounded-[10px] shadow-[0_1px_6px_rgba(35,35,35,0.15)] flex py-[15px] px-[10px] lg:p-[15px] cursor-pointer">
      <Spacing
        size="S"
        justifyContent="space-between"
        alignItems="center"
        mobileAlign={!hasIncomingInsurance ? 'center' : 'flex-start'}
        isResponsive={hasIncomingInsurance}
        fullWidth
      >
        <Spacing size="S" responsiveSize="XXS" alignItems="center" fullWidth>
          {Boolean(icon) && <Icon type={icon} mobileSize="S" />}

          {isBrandAware ? (
            <Spacing size="XS" vertical alignItems="flex-start">
              <Text mobileSize="S" weight="semibold">
                {t('insurance:Insurance Checkbox Title', { context: titleContext })}
              </Text>
              {/* An anchor to nowhere is worse than its absence, and the tenant can add the
                  key later without a deploy. The label carries the brand's context because
                  Rápido Ochoa calls the document by its own name; i18next falls back to the
                  generic wording for any brand without a variant. */}
              {Boolean(detailsUrl) && (
                <Link
                  text={t('insurance:terms_conditions', { context: brand })}
                  href={detailsUrl}
                  type="primary"
                  newTab
                  size="S"
                />
              )}
            </Spacing>
          ) : (
            <InsuranceModal />
          )}
        </Spacing>

        <Spacing alignItems="center" justifyContent="space-between" fullWidth>
          {insuranceOutgoingUnitAmount > 0 && (
            <Field
              id={`${passenger}.wantsOutgoingInsurance`}
              name={`${passenger}.wantsOutgoingInsurance`}
              // Without `type`, redux-form passes `value` and never `checked`, so the box renders
              // but never reflects state — and nothing fails.
              type="checkbox"
              component={checkboxRenderer}
              label={legLabel(departureInsuranceLabel, insuranceOutgoingUnitAmount)}
              disabled={isLocked}
            />
          )}

          {hasIncomingInsurance && (
            <Field
              id={`${passenger}.wantsIncomingInsurance`}
              name={`${passenger}.wantsIncomingInsurance`}
              type="checkbox"
              component={checkboxRenderer}
              label={legLabel('label.returning_trip', insuranceIncomingUnitAmount)}
              disabled={isLocked}
            />
          )}
        </Spacing>
      </Spacing>
    </div>
  );
};

PassengerInsuranceToggle.propTypes = {
  passenger: PropTypes.string.isRequired,
  insuranceOutgoingUnitAmount: PropTypes.number.isRequired,
  insuranceIncomingUnitAmount: PropTypes.number,
  disabled: PropTypes.bool,
};

PassengerInsuranceToggle.defaultProps = {
  insuranceIncomingUnitAmount: 0,
  disabled: false,
};

export default PassengerInsuranceToggle;
